import type { ClientOutputs } from '@botpress/client'
import type { LucideIcon } from 'lucide-react'
import { getApiClient } from '~/stores'

const client = getApiClient()

export type BotV2 = Awaited<ReturnType<typeof client.getBot>>['bot'] & {
  type: 'V2'
  deployedAt?: string // TODO update the type from skynet
}

export type BotV2Partial = Awaited<ReturnType<typeof client.listBots>>['bots'][number] & {
  type: 'V2'
}

export type BotObject = ClientOutputs['getBot']['bot']
export type BotSummary = ClientOutputs['listBots']['bots'][number]

export type BotStatus = 'pending' | 'starting' | 'active' | 'updating' | 'restricted' | 'deleted' | 'unknown'

export type BotApiKey = ClientOutputs['createBotApiKey']

export type Bot = {
  id: string
  status: {
    value: BotStatus
    label: string
  }
  runtimeName: string
  workspaceId: string
  name: string
  configurations: BotConfiguration | null
  createdByUserId: string
  createdOn: string
  updatedOn: string
  importedOn: string | null
  type: 'V1'
}

type BotConfiguration = {
  cloud: BotCloudConfiguration
  messaging: BotMessagingConfiguration
  showBotInfoPage?: boolean
  name?: string
  description?: string
  defaultLanguage?: string
  details?: {
    website?: string
    phoneNumber?: string
    termsConditions?: string
    privacyPolicy?: string
    emailAddress?: string
    coverPictureUrl?: string
    avatarUrl?: string
  }
}

type BotCloudConfiguration = {
  clientId: string
  clientSecret: string
}

type BotMessagingConfiguration = {
  clientId: string
  clientToken: string
  webhookToken: string | null
  channels?: Record<string, Record<string, unknown>>
}

export type GetLogsResponse = {
  logs: Log[]
}

export type Log = {
  timestamp: Date
  level: string
  message: string
  tags: Record<string, string>
}

export type BotAnalyticsRecord = {
  startDateTimeUtc: string
  endDateTimeUtc: string
  returningUsers: number
  newUsers: number
  sessions: number
  messages: number
  botMessages: number
  userMessages: number
}

export const BotAnalyticsMetricKeys = ['messages', 'newUsers', 'sessions'] as const
export type BotAnalyticsMetric = (typeof BotAnalyticsMetricKeys)[number]

export type BotAnalyticsMetrics = Pick<
  BotAnalyticsRecord,
  'returningUsers' | 'newUsers' | 'sessions' | 'botMessages' | 'userMessages' | 'messages'
>

export type BotAnalyticsResponse = {
  records: BotAnalyticsRecord[]
}

export type OnboardingStep = {
  id: number
  title: string
  icon: LucideIcon
}
